// src/features/editor/components/CourseSettingsDialog.tsx
import { useEffect, useState } from 'react'
import { Badge, Dialog, DialogContent, DialogHeader, DialogTitle } from '@teach/ui'
import { UnderlineTabs } from '@/components/common/UnderlineTabs'
import { courseDisplayName } from '@/lib/localizedText'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useGoToLessonSettings } from '@/features/editor/components/studio/useLessonLastBlock'
import {
  selectBlock,
  selectLesson,
  setDialogOpen,
  setSettingsLang,
  setContentLang,
} from '../editorSlice'
import { useCourseSettingsForm } from './course-settings/useCourseSettingsForm'
import { CourseSettingsContent } from './course-settings/CourseSettingsContent'
import { TABS } from './course-settings/tabs'
import type { TabKey } from './course-settings/tabs'
import type { ViolationGo } from './publish/violationPlace'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
}

export const CourseSettingsDialog = ({ course }: Props) => {
  const m = useMessages(messages)
  const dispatch = useAppDispatch()
  const goToLessonSettings = useGoToLessonSettings()
  // Название — на языке правки: окно живёт внутри редактора и правит этот слот.
  const contentLang = useAppSelector((st) => st.editor.contentLang)
  const open = useAppSelector((s) => s.editor.dialogs.settingsOpen)
  const requestedTab = useAppSelector((s) => s.editor.settingsTab)
  const [activeTab, setActiveTab] = useState<TabKey>('basic')
  const { local, setLocal, save, flushPendingChanges } = useCourseSettingsForm(course, open)

  // Вкладка сбрасывается на первую при КАЖДОМ открытии: диалог смонтирован
  // всегда, и без сброса «ведите автора в поле стиля» (§6 спеки, ссылка из
  // DrawImagesDialog) приводило бы его на вкладку, открытую в прошлый раз.
  useEffect(() => {
    if (!open) return
    const wanted = TABS.find((t) => t.key === requestedTab)
    setActiveTab(wanted ? wanted.key : 'basic')
  }, [open, requestedTab])

  // Нарушение курса правится здесь же, во «Основном»; урок и блок — в редакторе под
  // окном, поэтому окно закрывается тем же путём, что и крестик: с flush формы.
  const goToViolation = (target: ViolationGo) => {
    if (target.kind === 'course') {
      setActiveTab(target.tab ?? 'basic')
      if (target.lang) dispatch(setSettingsLang(target.lang))
      return
    }
    flushPendingChanges()
    dispatch(setDialogOpen({ dialog: 'settingsOpen', open: false }))
    if (target.lang) dispatch(setContentLang(target.lang))
    // selectLesson гасит выбранный блок — блок выбираем ПОСЛЕ него.
    // «Перейти» к уроку (не к блоку) — в настройки урока, а не на его экран.
    if (target.kind === 'lesson') {
      goToLessonSettings(target.lessonId)
      return
    }
    dispatch(selectLesson(target.lessonId))
    dispatch(selectBlock(target.blockId))
  }

  const tabs = TABS.map((t) => ({
    id: t.key,
    label: t.label,
    icon: t.icon,
    extra: t.soon ? <Badge variant="neutral">{m.soon}</Badge> : null,
  }))

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) flushPendingChanges()
        dispatch(setDialogOpen({ dialog: 'settingsOpen', open: next }))
      }}
    >
      {/* Та же раскладка, что у страницы настроек (`CourseSettingsPage`): название курса,
          вкладки верхним меню, секции без своих заголовков. */}
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-4 px-8 pt-8">
          <DialogTitle className="text-3xl font-semibold">
            {courseDisplayName(local, contentLang)}
          </DialogTitle>
          <UnderlineTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
        </DialogHeader>
        <div className="h-[600px] overflow-y-auto px-8 pb-8 pt-8">
          <CourseSettingsContent
            course={course}
            activeTab={activeTab}
            local={local}
            setLocal={setLocal}
            save={save}
            showHeading={false}
            onNavigate={goToViolation}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
